import React, { useState } from 'react';

const getPlatformColor = (platform) => {
  const colors = { toonation: '#00B4D8', funnation: '#7C3AED', youtube: '#FF0000' };
  return colors[platform] || '#8B8B9E';
};

const getPlatformLabel = (platform) => {
  const labels = { toonation: '투네이션', funnation: '펀네이션', youtube: '유튜브' };
  return labels[platform] || platform;
};

const formatAmountShared = (amount) => new Intl.NumberFormat('ko-KR').format(amount);

const getTierColor = (tier) => {
  if (tier === 'whale') return '#FF6B6B';
  if (tier === 'potential') return '#F59E0B';
  if (tier === 'regular') return '#3B82F6';
  return null;
};

// 렌더 함수 밖으로 분리 (React reconciliation 최적화)
function MiniEventItem({ event }) {
  if (event._type === 'donation') {
    const isBigSpender = event.tier && event.tier !== 'normal';
    const tierColor = getTierColor(event.tier);
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem',
        background: isBigSpender ? (tierColor + '08') : 'transparent',
        borderLeft: isBigSpender ? `3px solid ${tierColor}` : 'none',
        paddingLeft: isBigSpender ? 8 : 0,
        marginLeft: isBigSpender ? -4 : 0,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: isBigSpender ? tierColor : getPlatformColor(event.platform), flexShrink: 0,
        }} />
        <span style={{ flex: 1 }}>
          <strong>{event.nickname}</strong>
          <span style={{ color: '#F59E0B', marginLeft: 4 }}>{formatAmountShared(event.amount)}원</span>
          {isBigSpender && (event.todayDonated > 0 || event.totalDonated > 0) && (
            <span style={{ color: '#8B8B9E', fontSize: '0.75rem', marginLeft: 4 }}>
              ({event.todayDonated > 0 ? `오늘 ${formatAmountShared(event.todayDonated)}원` : ''}
              {event.todayDonated > 0 && event.totalDonated > 0 ? ' · ' : ''}
              {event.totalDonated > 0 ? `누적 ${formatAmountShared(event.totalDonated)}원` : ''}
              {event.nativeTotal ? '' : ''})
            </span>
          )}
        </span>
        <span style={{ color: '#8B8B9E', fontSize: '0.75rem' }}>
          {getPlatformLabel(event.platform)}
        </span>
      </div>
    );
  }

  const isEnter = event._type === 'big_spender_enter';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem',
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: isEnter ? '#00D26A' : '#FF4757', flexShrink: 0,
      }} />
      <span style={{ flex: 1 }}>
        <strong>{event.viewer_nickname}</strong>
        {isEnter ? ' 입장' : ' 퇴장'}
      </span>
      <span style={{ color: '#8B8B9E', fontSize: '0.75rem' }}>
        {event.created_at ? new Date(event.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : ''}
      </span>
    </div>
  );
}

/**
 * 실시간 알림 패널 컴포넌트
 */
function NotificationPanel({ notifications, realtimeDonations, activeSpenders, connected, onClear }) {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('donations'); // 'donations' | 'alerts'

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}분`;
    const hours = Math.floor(minutes / 60);
    return `${hours}시간 ${minutes % 60}분`;
  };

  const formatAmount = formatAmountShared;

  const getTierLabel = (tier) => {
    const labels = { whale: '큰손', potential: '잠재큰손', regular: '단골', normal: '일반' };
    return labels[tier] || '일반';
  };

  // 실시간 후원 + 입장/퇴장을 시간순 병합
  const allEvents = [
    ...(realtimeDonations || []).map(d => ({
      ...d,
      _type: 'donation',
      _time: new Date(d.timestamp).getTime(),
    })),
    ...(notifications || []).filter(n =>
      n.event_type === 'big_spender_enter' || n.event_type === 'big_spender_exit'
    ).map(n => ({
      ...n,
      _type: n.event_type,
      _time: new Date(n.created_at || n.timestamp).getTime(),
    })),
  ].sort((a, b) => b._time - a._time).slice(0, 5);

  return (
    <>
      {/* 미니 카드 */}
      <div className="card" style={{ cursor: 'pointer' }} onClick={() => setShowModal(true)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ fontSize: '1rem' }}>실시간 피드</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`} />
            <span style={{ fontSize: '0.8rem', color: '#8B8B9E' }}>
              {connected ? '연결됨' : '대기'}
            </span>
          </div>
        </div>

        {/* 활성 큰손 표시 */}
        {activeSpenders && activeSpenders.length > 0 && (
          <div style={{
            background: 'rgba(0, 210, 106, 0.1)',
            border: '1px solid rgba(0, 210, 106, 0.3)',
            borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: '0.8rem',
          }}>
            <strong style={{ color: '#00D26A' }}>활성 큰손 {activeSpenders.length}명</strong>
          </div>
        )}

        {/* 최근 5건 (후원 + 입장/퇴장 통합) */}
        {allEvents.length > 0 ? (
          allEvents.map((e, i) => <MiniEventItem key={`${e._type}_${e._time}_${i}`} event={e} />)
        ) : (
          <p style={{ color: '#8B8B9E', fontSize: '0.85rem', textAlign: 'center', padding: '16px 0' }}>
            후원이 들어오면 여기에 실시간으로 표시됩니다.
          </p>
        )}

        {(allEvents.length > 0) && (
          <div style={{ textAlign: 'center', marginTop: 8, color: 'var(--primary)', fontSize: '0.8rem' }}>
            전체 보기 →
          </div>
        )}
      </div>

      {/* 전체 모니터링 모달 */}
      {showModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 600, width: '90%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2>실시간 모니터링</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                {onClear && (
                  <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={onClear}>
                    초기화
                  </button>
                )}
                <button className="btn btn-outline" style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={() => setShowModal(false)}>
                  닫기
                </button>
              </div>
            </div>

            {/* 탭 */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                className={`btn ${activeTab === 'donations' ? 'btn-primary' : 'btn-outline'}`}
                style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                onClick={() => setActiveTab('donations')}
              >
                후원 피드 ({(realtimeDonations || []).length})
              </button>
              <button
                className={`btn ${activeTab === 'alerts' ? 'btn-primary' : 'btn-outline'}`}
                style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                onClick={() => setActiveTab('alerts')}
              >
                큰손 알림 ({(notifications || []).length})
              </button>
            </div>

            {/* 후원 피드 탭 */}
            {activeTab === 'donations' && (
              <div>
                {(realtimeDonations || []).length > 0 ? (
                  realtimeDonations.map((d, i) => {
                    const isBig = d.tier && d.tier !== 'normal';
                    const tColor = getTierColor(d.tier);
                    return (
                    <div key={`${d.timestamp}_${d.amount}_${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 8px', borderBottom: '1px solid var(--border)',
                      background: isBig ? (tColor + '0A') : 'transparent',
                      borderLeft: isBig ? `3px solid ${tColor}` : 'none',
                      borderRadius: isBig ? 4 : 0,
                    }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: isBig ? tColor : getPlatformColor(d.platform), flexShrink: 0,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div>
                          <strong>{d.nickname}</strong>
                          <span style={{
                            fontSize: '0.75rem', marginLeft: 8, padding: '1px 6px',
                            background: getPlatformColor(d.platform) + '20',
                            color: getPlatformColor(d.platform),
                            borderRadius: 4,
                          }}>
                            {getPlatformLabel(d.platform)}
                          </span>
                          {isBig && (
                            <span className={`tier-badge tier-${d.tier}`} style={{ marginLeft: 4 }}>
                              {getTierLabel(d.tier)}
                            </span>
                          )}
                        </div>
                        {d.message && (
                          <div style={{ color: '#8B8B9E', fontSize: '0.8rem', marginTop: 2 }}>{d.message}</div>
                        )}
                        {isBig && (d.todayDonated > 0 || d.totalDonated > 0) && (
                          <div style={{ fontSize: '0.75rem', marginTop: 3, color: '#8B8B9E' }}>
                            {d.todayDonated > 0 && (
                              <span>
                                오늘 <strong style={{ color: tColor }}>{formatAmount(d.todayDonated)}원</strong>
                                {d.nativeTodayDonated != null && <span style={{ color: '#00B4D8', marginLeft: 2 }}>(투네이션)</span>}
                              </span>
                            )}
                            {d.todayDonated > 0 && d.totalDonated > 0 && <span> · </span>}
                            {d.totalDonated > 0 && (
                              <span>
                                누적 {formatAmount(d.totalDonated)}원
                                {d.nativeTotal && <span style={{ color: '#00B4D8', marginLeft: 2 }}>(투네이션)</span>}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#F59E0B', fontWeight: 700 }}>{formatAmount(d.amount)}원</div>
                        <div style={{ color: '#8B8B9E', fontSize: '0.75rem' }}>
                          {d.timestamp ? new Date(d.timestamp).toLocaleTimeString('ko-KR') : ''}
                        </div>
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <p style={{ color: '#8B8B9E', fontSize: '0.85rem', textAlign: 'center', padding: '24px 0' }}>
                    실시간 후원이 들어오면 여기에 표시됩니다.
                  </p>
                )}
              </div>
            )}

            {/* 큰손 알림 탭 */}
            {activeTab === 'alerts' && (
              <div>
                {/* 활성 큰손 */}
                {activeSpenders && activeSpenders.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={{ fontSize: '0.9rem', marginBottom: 8, color: '#00D26A' }}>
                      현재 활성 큰손 ({activeSpenders.length}명)
                    </h3>
                    {activeSpenders.map((s, i) => (
                      <div key={s.channelId || i} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '8px 10px', marginBottom: 4,
                        background: 'rgba(0, 210, 106, 0.05)',
                        borderRadius: 8, border: '1px solid rgba(0, 210, 106, 0.2)',
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00D26A' }} />
                        <div style={{ flex: 1 }}>
                          <strong>{s.nickname}</strong>
                          <span className={`tier-badge tier-${s.tier || 'normal'}`} style={{ marginLeft: 6 }}>
                            {getTierLabel(s.tier)}
                          </span>
                        </div>
                        <span style={{ color: '#8B8B9E', fontSize: '0.8rem' }}>
                          체류 {formatDuration(s.stayDuration)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* 입장/퇴장 로그 */}
                {(notifications || []).length > 0 ? (
                  notifications.map((n, i) => {
                    const isEnter = n.event_type === 'big_spender_enter';
                    const isExit = n.event_type === 'big_spender_exit';
                    const details = n.details ? (typeof n.details === 'string' ? JSON.parse(n.details) : n.details) : {};

                    return (
                      <div key={n.id || i} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '8px 0', borderBottom: '1px solid var(--border)',
                      }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: isEnter ? '#00D26A' : '#FF4757',
                        }} />
                        <div style={{ flex: 1 }}>
                          <strong>{n.viewer_nickname}</strong>
                          <span className={`tier-badge tier-${n.viewer_tier || 'normal'}`} style={{ marginLeft: 6 }}>
                            {getTierLabel(n.viewer_tier)}
                          </span>
                          <div style={{ color: '#8B8B9E', fontSize: '0.8rem', marginTop: 2 }}>
                            {isEnter && (details.isReentry ? '재입장' : '입장')}
                            {isExit && `퇴장 (체류 ${formatDuration(details.duration)})`}
                          </div>
                        </div>
                        <span style={{ color: '#8B8B9E', fontSize: '0.8rem' }}>
                          {n.created_at ? new Date(n.created_at).toLocaleString('ko-KR') : ''}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p style={{ color: '#8B8B9E', fontSize: '0.85rem', textAlign: 'center', padding: '24px 0' }}>
                    아직 기록된 이벤트가 없습니다.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default NotificationPanel;
